import { useForm } from "@inertiajs/react";
import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { bulk_create_bizminer_admin_organization_users_path } from "@/routes";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
}

function countLines(text: string): number {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean).length;
}

export default function BulkCreateDialog({
  open,
  onOpenChange,
  organizationId,
}: Props) {
  const [errors, setErrors] = useState<string[]>([]);

  const { data, setData, post, processing, reset } = useForm({
    users_text: "",
  });

  const lineCount = countLines(data.users_text);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    post(bulk_create_bizminer_admin_organization_users_path(organizationId), {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        handleClose();
      },
      onError: (errors) => {
        const baseErrors = errors.base as string[] | undefined;
        if (baseErrors) {
          setErrors(baseErrors);
        }
      },
    });
  };

  const handleClose = () => {
    reset();
    setErrors([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Bulk Add Users</DialogTitle>
            <DialogDescription>
              Add multiple users at once. One user per line.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {errors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <ul className="list-disc list-inside space-y-1">
                    {errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>Users (one per line)</Label>
              <Textarea
                value={data.users_text}
                onChange={(e) => setData("users_text", e.target.value)}
                placeholder={
                  "John Doe, john@example.com\nJane Smith, jane@example.com\nbob@example.com"
                }
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Format: "First Last, email@example.com" or just
                "email@example.com"
              </p>
            </div>

            {lineCount > 0 && (
              <p className="text-sm text-muted-foreground">
                {lineCount} {lineCount === 1 ? "user" : "users"} will be created
                with role "Viewer"
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing || lineCount === 0}>
              {processing ? "Creating..." : `Create ${lineCount} Users`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
